import React, { useState, useEffect, useRef } from 'react';
import axiosInstance from '../../components/auth/auth';
import { Container, Spinner, Alert, ButtonGroup, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import './ListStation.css';
import { TextField, InputAdornment } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

const StationListPage = () => {
    const [stations, setStations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [allStations, setAllStations] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;
    const navigate = useNavigate();
    const hasFetched = useRef(false);
    const hasStatus = useRef(false);
    const [isStatusLoading, setIsStatusLoading] = useState(true);
    const [filterStatus, setFilterStatus] = useState('All'); // New state for filtering

    useEffect(() => {
        const fetchStations = async () => {
            if (hasFetched.current) return;
            hasFetched.current = true;
            try {
                const response = await axiosInstance.get(global.config.server_url + 'api/get/list/stations/');
                const fetchedData = response.data;

                // Step 1: show data immediately
                setStations(fetchedData);
                setLoading(false);
                setIsStatusLoading(true);

                // Step 2: collect serial numbers and update statuses
                const allSerialNumbers = [];
                fetchedData.forEach(farm => {
                    farm.sensor_stations.forEach(station => {
                        allSerialNumbers.push(station.serial_number);
                    });
                });

                updateStatusesBatch(allSerialNumbers, fetchedData); // <-- Pass fetched data
            } catch (error) {
                console.error('Failed to fetch station list', error);
                setLoading(false);
            }
        };

        fetchStations();
    }, []);

    const updateStatusesBatch = async (serialNumbers, currentData) => {
        if (hasStatus.current) return;
        hasStatus.current = true;
        try {
            const res = await axiosInstance.post(
                global.config.server_url + 'api/get/stations/status/',
                { serial_numbers: serialNumbers }
            );

            const statusMap = res.data.data;

            const updatedFarms = currentData.map(farm => ({
                ...farm,
                sensor_stations: farm.sensor_stations.map(station => ({
                    ...station,
                    is_active: statusMap[station.serial_number] === 'Online'
                }))
            }));

            setStations(updatedFarms);
            setIsStatusLoading(false);
        } catch (err) {
            console.error("Failed to batch update statuses", err);
            setIsStatusLoading(false);
        }
    };



    if (loading) return <div className="text-center mt-5"><Spinner animation="border" /></div>;



    const filteredStations = stations.flatMap((farm) =>
        farm.sensor_stations
            .filter((station) => {
                const matchesSearch =
                    station.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    station.serial_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    farm.farm_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    station.location?.toLowerCase().includes(searchTerm.toLowerCase());

                const matchesFilter =
                filterStatus === 'All' ||
                (filterStatus === 'Online' && (!isStatusLoading ? station.is_active : true)) ||
                (filterStatus === 'Offline' && (!isStatusLoading ? !station.is_active : true));

                return matchesSearch && matchesFilter;
            })
            .map((station) => ({
                ...station,
                farm_name: farm.farm_name
            }))
      );

    const totalPages = Math.ceil(filteredStations.length / itemsPerPage);
    const paginatedStations = filteredStations.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );
    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1); // reset to page 1 on search
    };



    const handleRowClickDetails = (station) => {
        const detailsUrl = `/stations/${station.serial_number}`;
        window.open(detailsUrl, "_blank");
    };
    const handleRowClickDevices = (station) => {
        const deviceUrl = `/devices/${station.serial_number}`;
        window.open(deviceUrl, '_blank');
    };

    const handleFilterStatusChange = (status) => {
        setFilterStatus(status);
        setCurrentPage(1); // Reset to page 1 when filter changes
    };

    return (
        <>
        <div className="row align-items-center mb-3">
            <div className="col-12 col-md-9">
                <h4><b>Sensor Station List</b></h4>

            </div>
            <div className="card">
              <div className="card-body">
                <div style={{ maxWidth: '100%' }}>
                  {/* Filter and Search Row */}
                  <div className="d-flex justify-content-between align-items-center flex-wrap mb-3">
                    {/* Left: Buttons */}
                    <div className="mb-2 mb-md-0">
                      <ButtonGroup>
                        <Button
                          variant={filterStatus === 'All' ? 'primary' : ''}
                          onClick={() => handleFilterStatusChange('All')}
                        >
                          All
                        </Button>
                        <Button
                          variant={filterStatus === 'Online' ? 'primary' : ''}
                          onClick={() => handleFilterStatusChange('Online')}
                        >
                          Online
                        </Button>
                        <Button
                          variant={filterStatus === 'Offline' ? 'primary' : ''}
                          onClick={() => handleFilterStatusChange('Offline')}
                        >
                          Offline
                        </Button>
                      </ButtonGroup>
                    </div>

                    {/* Right: Search */}
                    <div style={{ minWidth: '250px' }}>
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="Search"
                        value={searchTerm}
                        onChange={handleSearchChange}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '10px',
                          },
                        }}
                      />
                    </div>
                  </div>

                  {/* Table aligned with filter/search */}
                  <div style={{ width: '100%', overflowX: 'auto' }}>
                  <table className="table no-border-table mb-0">
                    <thead>
                      <tr>
                        <th>NO.</th>
                        <th>FARM</th>
                        <th>STATION NAME</th>
                        <th>SERIAL</th>
                        <th>STATUS</th>
                        <th>LOCATION</th>
                        <th>ACTION</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedStations.map((station, index) => (
                        <tr key={index}>
                          <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                          <td className="black-text">{station.farm_name}</td>
                          <td>{station.name}</td>
                          <td>{station.serial_number}</td>
                          <td>
                            {isStatusLoading ? (
                              <Spinner animation="border" size="sm" />
                            ) : (
                              <span className={`status ${station.is_active ? "status-online" : "status-offline"}`}>
                                {station.is_active ? "Online" : "Offline"}
                              </span>
                            )}
                          </td>
                          <td className="black-text">{station.location}</td>
                          <td>
                            <div className="button-group-fit">
                              <button
                                className="custom-btn-outline me-2 px-3 py-1"
                                onClick={() => handleRowClickDetails(station)}
                              >
                                Detail
                              </button>
                              <button
                                className="custom-btn-filled px-3 py-1"
                                onClick={() => handleRowClickDevices(station)}
                              >
                                Devices
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                  <div className="pt-1 row align-items-center mt-3">
                      <div className="col-12 col-md-6">
                        <p className="mb-2 mb-md-0">Total of {filteredStations.length} records</p>
                      </div>
                      <div className="col-12 col-md-6 d-flex justify-content-md-end flex-wrap">
                        {Array.from({ length: totalPages }, (_, i) => (
                        <button
                          key={i}
                          className={`btn btn-sm mx-1 mb-2 px-3 py-1 ${currentPage === i + 1 ? "custom-btn-filled" : "custom-btn-outline"}`}
                          style={{ maxWidth: "40px" }}
                          onClick={() => setCurrentPage(i + 1)}
                        >
                          {i + 1}
                        </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
      );
    };

export default StationListPage;